import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  fetchHoursMonthlyOverview,
  updateHoursMonthlyOverviewUnlock,
} from "../../../api/hour";
import { fetchDepartments } from "../../../api/department";
import { EmployeeSelector } from "../../../components/utils/moduleSelectors/employeeSelector";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { useCanManageHoursMonthlyOverview } from "../../../routes/roleOnly";

const monthInputValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

export const HoursMonthlyOverview = () => {
  const { t } = useTranslation();
  const canManageMonthlyOverview = useCanManageHoursMonthlyOverview();
  const [monthValue, setMonthValue] = useState(() => monthInputValue(new Date()));
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!canManageMonthlyOverview) {
      return undefined;
    }

    let active = true;
    fetchDepartments({ per_page: 500, page: 1 })
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setDepartments(list);
      })
      .catch(() => {
        if (active) setDepartments([]);
      });

    return () => {
      active = false;
    };
  }, [canManageMonthlyOverview]);

  useEffect(() => {
    if (!canManageMonthlyOverview) {
      return undefined;
    }

    let active = true;
    const [year, month] = monthValue.split("-");

    setIsLoading(true);
    fetchHoursMonthlyOverview({
      year,
      month,
      department_id: selectedDepartmentId || undefined,
      user_id: selectedUser?.id || undefined,
    })
      .then((res) => {
        if (!active) return;
        setOverview(res?.data?.data || null);
      })
      .catch(() => {
        if (active) setOverview(null);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canManageMonthlyOverview, monthValue, selectedDepartmentId, selectedUser?.id]);

  const rows = useMemo(() => overview?.users || [], [overview]);

  if (!canManageMonthlyOverview) {
    return <Navigate to="/dashboard/hours" replace />;
  }

  const handleToggleUnlock = async (row) => {
    const [year, month] = monthValue.split("-");

    try {
      await updateHoursMonthlyOverviewUnlock({
        user_id: row.user?.id,
        year,
        month,
        unlocked: !row.user_period_unlocked,
      });

      setOverview((prev) => {
        if (!prev?.users) {
          return prev;
        }

        return {
          ...prev,
          users: prev.users.map((userRow) => {
            if (userRow.user?.id !== row.user?.id) {
              return userRow;
            }

            const nextUnlocked = !userRow.user_period_unlocked;
            return {
              ...userRow,
              user_period_unlocked: nextUnlocked,
              period_locked: Boolean(userRow.base_period_locked) && !nextUnlocked,
            };
          }),
        };
      });

      toast.success(
        !row.user_period_unlocked
          ? t("Perioden er låst opp for brukeren.")
          : t("Brukerens opplåsing er fjernet.")
      );
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("Kunne ikke oppdatere periode."));
    }
  };

  return (
    <DashboardHeader
      title={t("Månedsoversikt timer")}
      subtitle={t("Oversikt per ansatt over normaltid, registrerte timer og prosjekter i valgt måned.")}
    >
      <div className="space-y-6 px-4 py-4">
        <div className="rounded-md bg-white p-4 shadow">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">{t("Periode")}</label>
              <input
                type="month"
                value={monthValue}
                onChange={(e) => setMonthValue(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">{t("Avdeling")}</label>
              <select
                value={selectedDepartmentId}
                onChange={(e) => setSelectedDepartmentId(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t("Alle avdelinger")}</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.displayTitle || department.title || department.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">{t("Ansatt")}</label>
              <EmployeeSelector
                name="user_id"
                selected={selectedUser}
                filters={[{ name: "employee", value: true }]}
                placeholder={t("Alle ansatte")}
                onChange={(data) => setSelectedUser(data?.target?.object || null)}
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <ResourceLoading />
        ) : (
          <div className="space-y-4">
            {rows.length === 0 ? (
              <div className="rounded-md bg-white p-6 text-sm text-gray-500 shadow">
                {t("Ingen timer funnet for valgt filter.")}
              </div>
            ) : (
              rows.map((row) => (
                <div key={row.user?.id} className="overflow-hidden rounded-md bg-white shadow">
                  <div className="border-b border-gray-200 px-4 py-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {row.user?.displayTitle || row.user?.name || t("Ukjent ansatt")}
                        </h3>
                        <p className="text-sm text-gray-500">{row.user?.email}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-medium ${
                            row.period_locked ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                          }`}
                        >
                          {row.period_locked ? t("Periode låst") : t("Periode åpen")}
                        </span>
                        {row.base_period_locked ? (
                          <button
                            type="button"
                            onClick={() => handleToggleUnlock(row)}
                            className={`inline-flex items-center rounded-md px-3 py-2 text-xs font-medium shadow-sm ${
                              row.user_period_unlocked
                                ? "bg-slate-700 text-white hover:bg-slate-800"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                          >
                            {row.user_period_unlocked ? t("Fjern opplåsing") : t("Lås opp for bruker")}
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {row.user_period_unlocked ? (
                      <p className="mt-2 text-xs text-blue-700">
                        {t("Denne brukeren er manuelt låst opp for valgt måned.")}
                      </p>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-1 gap-4 border-b border-gray-100 px-4 py-4 md:grid-cols-3">
                    <div className="rounded-md bg-slate-50 p-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("Normaltid")}</div>
                      <div className="mt-1 text-lg font-semibold text-slate-900">
                        {row.normal_hours_month != null ? `${row.normal_hours_month.toFixed(1)}h` : "—"}
                      </div>
                    </div>
                    <div className="rounded-md bg-slate-50 p-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("Registrert")}</div>
                      <div className="mt-1 text-lg font-semibold text-slate-900">
                        {`${(row.total_registered_hours || 0).toFixed(1)}h`}
                      </div>
                    </div>
                    <div className="rounded-md bg-slate-50 p-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("Avvik")}</div>
                      <div className="mt-1 text-lg font-semibold text-slate-900">
                        {`${(row.surplus_month || 0).toFixed(1)}h`}
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            {t("Prosjekt")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            {t("Status")}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            {t("Timer")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {(row.project_rows || []).length === 0 ? (
                          <tr>
                            <td colSpan={3} className="px-4 py-4 text-sm text-gray-500">
                              {t("Ingen timer registrert på prosjekter i denne perioden.")}
                            </td>
                          </tr>
                        ) : (
                          row.project_rows.map((projectRow, index) => (
                            <tr key={`${row.user?.id}-${projectRow.project?.id || index}`}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {projectRow.project?.displayTitle || projectRow.project?.title || t("Uten prosjekt")}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {projectRow.project?.status || "—"}
                              </td>
                              <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                                {`${(projectRow.total_hours || 0).toFixed(1)}h`}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </DashboardHeader>
  );
};
